// packages/frontend/src/features/shelves/ManageShelvesSheet.tsx
//
// "Manage shelves" bottom sheet (Plan 13, Task 9): drag-reorder every shelf,
// hide/show it, and delete custom shelves. Unlike deriveShelves (which drops
// hidden and empty shelves from the library home), this sheet lists ALL
// shelves — builtins + customs — so every interaction stays reachable no
// matter how many are currently hidden/empty. Every interaction is a
// read-modify-write against the freshest `useShelfConfig` snapshot, persisted
// as one complete `PutShelfConfigRequest` (whole-doc last-writer-wins).
import type { JSX } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, GripVertical, Loader2, Trash2 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BUILTIN_SHELF_IDS, type BuiltinShelfId, type ShelfConfig } from "@recipevault/shared";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/ui/sheet.js";
import { Button } from "@/ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";
import { friendlyError } from "../../shared/http.js";
import { sync } from "../../sync/SyncService.js";
import { useShelfConfig } from "./useShelfConfig.js";
import { BUILTIN_SHELF_NAMES, orderShelfIds } from "./deriveShelves.js";

export interface ManageShelvesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShelfRowData {
  id: string;
  name: string;
  builtin: boolean;
  hidden: boolean;
}

/**
 * Pure reorder: moves the item at `from` to `to` within `order`, returning a
 * new array. Out-of-range indices are a no-op (returns `order` unchanged,
 * same reference) — dnd-kit's own `arrayMove` doesn't guard bounds. This is
 * what handleDragEnd below calls, and it's directly unit-tested since
 * dnd-kit's drag interaction itself isn't jsdom-testable.
 */
export function applyReorder(order: string[], from: number, to: number): string[] {
  if (from < 0 || to < 0 || from >= order.length || to >= order.length) return order;
  return arrayMove(order, from, to);
}

/**
 * Row order matches deriveShelves' ordering rule (orderShelfIds, extracted to
 * deriveShelves.ts so both stay in sync). Unlike deriveShelves, nothing is
 * dropped here — every builtin + custom id gets exactly one row, hidden or
 * not, so every shelf stays reachable to manage.
 */
function buildRows(config: ShelfConfig): ShelfRowData[] {
  const hidden = new Set(config.hidden);
  const customNames = new Map(config.custom.map((c) => [c.id, c.name]));
  return orderShelfIds(config).map((id) => {
    const builtin = (BUILTIN_SHELF_IDS as readonly string[]).includes(id);
    return {
      id,
      name: builtin ? BUILTIN_SHELF_NAMES[id as BuiltinShelfId] : (customNames.get(id) ?? id),
      builtin,
      hidden: hidden.has(id),
    };
  });
}

function SortableRow({
  row,
  disabled,
  onToggleHidden,
  onRequestDelete,
}: {
  row: ShelfRowData;
  disabled: boolean;
  onToggleHidden: (id: string) => void;
  onRequestDelete: (row: ShelfRowData) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex min-h-12 items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 pr-2"
    >
      <button
        type="button"
        className="flex h-12 w-12 shrink-0 items-center justify-center text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing disabled:pointer-events-none disabled:opacity-40"
        aria-label={`Reorder ${row.name}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span className="flex-1 min-w-0 truncate text-sm text-zinc-100">{row.name}</span>
      <button
        type="button"
        onClick={() => { onToggleHidden(row.id); }}
        disabled={disabled}
        aria-label={row.hidden ? `Show ${row.name}` : `Hide ${row.name}`}
        aria-pressed={row.hidden}
        className="flex h-12 w-12 shrink-0 items-center justify-center text-zinc-400 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-40"
      >
        {row.hidden ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
      </button>
      {!row.builtin && (
        <button
          type="button"
          onClick={() => { onRequestDelete(row); }}
          disabled={disabled}
          aria-label={`Delete ${row.name}`}
          className="flex h-12 w-12 shrink-0 items-center justify-center text-zinc-400 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      )}
    </li>
  );
}

/**
 * Bottom sheet (Plan 13, Task 9) reachable from the library's shelves view.
 * Read-modify-write freshness: `config` always comes from the live
 * `useShelfConfig` snapshot for the render that's currently on screen (Dexie
 * updates flow back through it after every optimistic write), and `saving`
 * disables every row control (drag handle, eye, trash) for the duration of an
 * in-flight PUT — so a second interaction can't fire against a config
 * snapshot that's already stale relative to the first.
 */
export function ManageShelvesSheet({ open, onOpenChange }: ManageShelvesSheetProps): JSX.Element {
  const { config, loading } = useShelfConfig();
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ShelfRowData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // While the device's first syncShelfConfig() hasn't landed yet, `config` is
  // only the DEFAULT_SHELF_CONFIG-merged placeholder (empty order/hidden/
  // custom) — never build rows or allow a mutation off it: a hide/reorder/
  // delete in that window would PUT a whole-doc config derived from the
  // placeholder, wiping the household's real custom shelves and order
  // (whole-doc last-writer-wins). The render body below shows a loading
  // affordance instead of the row list until `loading` flips false.
  const rows = config && !loading ? buildRows(config) : [];
  const rowIds = rows.map((r) => r.id);

  async function persist(next: { order: string[]; hidden: string[]; custom: ShelfConfig["custom"] }): Promise<void> {
    if (!config || loading) return;
    setSaving(true);
    try {
      await sync.putShelfConfigOptimistic({
        schemaVersion: config.schemaVersion,
        order: next.order,
        hidden: next.hidden,
        custom: next.custom,
      });
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  function handleDragEnd(e: DragEndEvent): void {
    if (!config || saving || loading) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = rowIds.indexOf(String(active.id));
    const to = rowIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    void persist({ order: applyReorder(rowIds, from, to), hidden: config.hidden, custom: config.custom });
  }

  function handleToggleHidden(id: string): void {
    if (!config || saving || loading) return;
    const hidden = config.hidden.includes(id)
      ? config.hidden.filter((h) => h !== id)
      : [...config.hidden, id];
    void persist({ order: config.order, hidden, custom: config.custom });
  }

  async function handleConfirmDelete(): Promise<void> {
    if (!config || !deleteTarget || loading) return;
    const id = deleteTarget.id;
    await persist({
      order: config.order.filter((o) => o !== id),
      hidden: config.hidden.filter((h) => h !== id),
      custom: config.custom.filter((c) => c.id !== id),
    });
    setDeleteTarget(null);
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="pb-safe max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Manage shelves</SheetTitle>
            <SheetDescription>
              Drag to reorder, tap the eye to hide, or delete a custom shelf.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-4">
            {loading ? (
              <p className="py-8 text-center text-sm text-zinc-500">Loading shelves&hellip;</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                  <ul className="flex flex-col gap-2">
                    {rows.map((row) => (
                      <SortableRow
                        key={row.id}
                        row={row}
                        disabled={saving}
                        onToggleHidden={handleToggleHidden}
                        onRequestDelete={setDeleteTarget}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</DialogTitle>
            <DialogDescription>
              This removes the custom shelf from your library home. Recipes on it aren&rsquo;t affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => { void handleConfirmDelete(); }} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Delete shelf
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
